/**
 * This component will be used for anchor to list of brands starting with the particular alphabet
 *
 * @module views/components/AnchorList
 * @memberof -Common
 */
import './AnchorList.scss';

import React, { useCallback, useEffect, useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { Link_Huge } from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';

import { isInViewport } from '@ulta/utils/isInViewport/isInViewport';

import * as utils from './AnchorList';
/**
 * Represents a AnchorList component
 *
 * @method
 * @param {AnchorListProps} props - React properties passed from composition
 * @returns AnchorList
 */
export const AnchorList = function( props ){
  const { allBrandsTitle, navItems, selectedLetter, onLetterClick, brandAccessibilityLabel } = props;
  const anchorRef = useRef( null );


  const handleClick = ( letter ) => {
    onLetterClick && onLetterClick( letter );
  };

  const getUrl = ( { title } ) => `#${title === '#' ? 'hash' : title}`;

  const getAriaLabel = title => props.brandAccessibilityLabel ? `${brandAccessibilityLabel} ${title}` : title;

  const anchorListCallbackHandler = useCallback( () => utils.composeAnchorListCallback( { anchorRef } ), [selectedLetter] );

  useEffect( anchorListCallbackHandler, [anchorListCallbackHandler] );

  return (
    <div className='AnchorList'
      ref={ anchorRef }
    >
      <Text textStyle='title-6'
        htmlTag='h2'
      >
        { allBrandsTitle }
      </Text>
      <ul>
        { navItems?.length > 0 &&
          navItems?.map( ( item, index ) => (
            <li key={ index }>
              <Link_Huge
                withHover
                url={ getUrl( item ) }
                disabled={ item.items?.length === 0 }
                ariaLabel={ getAriaLabel( item.title ) }
                target='_self'
                tabIndex={ item.items?.length === 0 ? -1 : null }
                onClick={ () => handleClick( item.title ) }
              >
                <span
                  className={ classNames( 'AnchorList__brand', {
                    [ANCHOR_LIST_ACTIVE_CLASS]: item.title === selectedLetter,
                    'AnchorList__brand--disabled': item.items?.length === 0
                  } ) }
                >
                  { item.title }
                </span>
              </Link_Huge>
            </li>
          ) ) }
      </ul>
    </div>
  );
};

/**
 * ShopAllBrands requires this class to get the active element
 * for horizontal scrolling
 *
 * @const {string} ANCHOR_LIST_ACTIVE_CLASS - List item's active class
 */
export const ANCHOR_LIST_ACTIVE_CLASS = 'AnchorList__brand--active';

/**
 * ShopAllBrands requires this class to get the ul element
 *
 * @const {string} ANCHOR_LIST_UL - List item's ul class
 */
export const ANCHOR_LIST_UL = 'AnchorList ul';


/**
 * Scrolls if the selected item is not in viewport
 * @method scrollToActiveElement
 * @param {object} data - Data arguments
 * @param {HTMLElement} data.element - HTML element
 * @param {HTMLElement} data.anchorListEl - Anchor list HTML element
 */
export const scrollToActiveElement = ( data )=>{
  const { element, anchorListEl } = data || {};
  if( !element || !anchorListEl ){
    return;
  }
  const isWithinView = isInViewport( { element } );
  const anchorList = anchorListEl;
  if( !isWithinView && anchorList instanceof Element && element instanceof Element ){
    const listDimensions = anchorList?.getBoundingClientRect();
    const selectedItem = element?.getBoundingClientRect();
    if( selectedItem?.left < 0 ){
      anchorList.scrollLeft += ( selectedItem?.left - listDimensions?.right ) + 300;
    }
    else {
      anchorList.scrollLeft += ( selectedItem?.left - listDimensions?.right ) + 100;
    }

  }
};

/**
 * call on active letter changes
 * @method composeAnchorListCallback
 * @param {object} data - Data arguments
 * @param {object} data.anchorRef - AnchorList Ref
 */
export const composeAnchorListCallback = ( data ) => {
  const { anchorRef } = data || {};
  if( !anchorRef?.current ){
    return;
  }
  const anchorListActiveClass = anchorRef.current.querySelector( `.${utils.ANCHOR_LIST_ACTIVE_CLASS}` );
  const anchorListUl = anchorRef.current.querySelector( `.${utils.ANCHOR_LIST_UL}` );
  utils.scrollToActiveElement( { element: anchorListActiveClass, anchorListEl: anchorListUl } );
};

/**
 * Property type definitions
 * @typedef AnchorListProps
 * @type {object}
 * @property {string} allBrandsTitle - Sets the title of the brands
 * @property {function} onLetterClick - On letter click handler
 * @property {string} selectedLetter - The currently selected letter to make active, pass empty to remove active class
 * @property {array} navItems - List of All Brands to display
 * @property {string} brandAccessibilityLabel - Sets the aria label of the brands
 */
export const propTypes = {
  allBrandsTitle: PropTypes.string,
  onLetterClick: PropTypes.func,
  selectedLetter: PropTypes.string,
  brandAccessibilityLabel: PropTypes.string,
  navItems: PropTypes.arrayOf(
    PropTypes.shape( {
      title: PropTypes.string,
      action: PropTypes.object,
      items: PropTypes.arrayOf(
        PropTypes.shape( {
          action: PropTypes.object,
          badges: PropTypes.array
        } )
      )
    } )
  )
};

AnchorList.propTypes = propTypes;

export default AnchorList;
